import { isTemplatePlaceholder, SearchTemplate, SearchTreeNode } from '@/types';
import { sortPlaceholdersByPosition } from '@/util';
import { produce } from 'immer';

import {
  findPlaceholdersInFlatSearchTree,
  FlatSearchTree,
  FlatSearchTreeLeafNode,
  flattenSearchTree,
  getValueAtPathFromFlatNode,
  isFlatSearchTreeLeafNode,
  updateFlatSearchTreeNodeFilterFieldValueInPlace,
} from '@components/LoadSearchTemplateDialog/flatSearchTree';
import { FieldMetaData } from '@components/LoadSearchTemplateDialog/templateFormControls/types';
import { parseSearchTemplateTree } from '@components/LoadSearchTemplateDialog/util';

import type { FilterFieldChangePayload } from './types';
import { fieldHash } from './types';

const OPTIONAL_FILTER_FIELDS = [
  'task.note',
  'engagementHistory.subject',
  'callingStatus.callingStatusListIds',
  'callingStatus.callingStatusUserSubs',
  'callingStatus.callingStatusCampaignIds',
  'eventAttendance.eventTypeIds',
  'eventAttendance.eventIds',
  'eventAttendance.eventCampaignIds',
  'eventAttendance.statusIds',
  'eventAttendance.eventDateRange.startDate',
  'eventAttendance.eventDateRange.endDate',
  'membership.branchIds',
] as const;

export type BuildInitialFlatSearchTreeResult = {
  initialFlatSearchTemplateTree: FlatSearchTree;
  placeholders: ReturnType<typeof findPlaceholdersInFlatSearchTree>;
  initialMetaDataOfPlaceholderFields: Record<string, FieldMetaData>;
};

export function buildInitialFlatSearchTree(
  searchTemplate: SearchTemplate,
  fromRestart: boolean,
  previousSelectionsTree?: SearchTreeNode
): BuildInitialFlatSearchTreeResult {
  let initialFlatSearchTemplateTree = flattenSearchTree(parseSearchTemplateTree(searchTemplate));

  const unsortedPlaceholders = findPlaceholdersInFlatSearchTree(initialFlatSearchTemplateTree);
  const placeholders = sortPlaceholdersByPosition(unsortedPlaceholders);

  const flatPreviousTree =
    fromRestart && previousSelectionsTree ? flattenSearchTree(previousSelectionsTree) : null;

  const initialMetaDataOfPlaceholderFields: Record<string, FieldMetaData> = {};

  for (const { nodeId, filterNameAndField } of placeholders) {
    const hasValueFromPrevious =
      flatPreviousTree &&
      (() => {
        const prevNode = flatPreviousTree.nodes[nodeId];
        if (!prevNode || !isFlatSearchTreeLeafNode(prevNode)) return false;
        const value = getValueAtPathFromFlatNode(prevNode, filterNameAndField);
        return value !== undefined && !isTemplatePlaceholder(value);
      })();

    if (hasValueFromPrevious && flatPreviousTree) {
      const prevNode = flatPreviousTree.nodes[nodeId] as FlatSearchTreeLeafNode;
      let value = getValueAtPathFromFlatNode(prevNode, filterNameAndField);
      if (
        filterNameAndField === 'callingStatus.callingStatusDateRange.callingPeriodQuantity' &&
        value !== undefined &&
        !isTemplatePlaceholder(value)
      ) {
        const dateRange = getValueAtPathFromFlatNode(
          prevNode,
          'callingStatus.callingStatusDateRange'
        ) as Record<string, unknown> | undefined;
        if (dateRange?.callingPeriodQuantity !== undefined && dateRange?.callingPeriodDetails) {
          value = {
            callingPeriodQuantity: dateRange.callingPeriodQuantity,
            callingPeriodDetails: dateRange.callingPeriodDetails,
          };
        }
      }
      const ourFieldMetadata = { state: 'valid', error: null } as FieldMetaData;
      initialMetaDataOfPlaceholderFields[fieldHash(nodeId, filterNameAndField)] = ourFieldMetadata;
      const ourPayload = { filterNameAndField, value, fieldMetaData: ourFieldMetadata } as FilterFieldChangePayload;
      initialFlatSearchTemplateTree = produce(initialFlatSearchTemplateTree, (draft) =>
        updateFlatSearchTreeNodeFilterFieldValueInPlace(draft, nodeId, ourPayload)
      );
    } else if ((OPTIONAL_FILTER_FIELDS as readonly string[]).includes(filterNameAndField)) {
      const ourFieldMetadata = { state: 'valid', error: null } as FieldMetaData;
      initialMetaDataOfPlaceholderFields[fieldHash(nodeId, filterNameAndField)] = ourFieldMetadata;
      const ourDefaultValue = ['task.note', 'engagementHistory.subject'].includes(filterNameAndField)
        ? ''
        : ['eventAttendance.eventDateRange.startDate', 'eventAttendance.eventDateRange.endDate'].includes(
              filterNameAndField
            )
          ? undefined
          : [];
      const ourPayload = {
        filterNameAndField,
        value: ourDefaultValue,
        fieldMetaData: ourFieldMetadata,
      } as FilterFieldChangePayload;
      initialFlatSearchTemplateTree = produce(initialFlatSearchTemplateTree, (draft) =>
        updateFlatSearchTreeNodeFilterFieldValueInPlace(draft, nodeId, ourPayload)
      );
    } else {
      initialMetaDataOfPlaceholderFields[fieldHash(nodeId, filterNameAndField)] = {
        state: 'empty',
        error: null,
      };
    }
  }

  return { initialFlatSearchTemplateTree, placeholders, initialMetaDataOfPlaceholderFields };
}
